import type { Dictionary } from '@/i18n/de'

export const en: Dictionary = {
    meta: {
        homeDescription: "Luxury holiday villas with or without a pool, holiday homes and apartments in Zarzis and on Djerba. Non-binding requests, personal service from Tunisia.",
        accommodationsTitle: "Accommodations",
        accommodationsDescription: "Villas with or without a swimming pool, holiday homes and apartments in Zarzis and on Djerba. Find your accommodation in Tunisia.",
        bookingTitle: "Booking request",
        bookingDescription: "Send us your non-binding booking request for a villa, holiday home or apartment in Tunisia.",
        contactTitle: "Contact",
        contactDescription: "Contact URLAUB DE LUXE TUNESIEN in El Mouensa, Zarzis. Questions, advice and booking requests.",
        loginTitle: "Sign in",
        registerTitle: "Register",
        verifyTitle: "Confirm email",
        forgotTitle: "Forgot password",
        privacyTitle: "Privacy",
        imprintTitle: "Legal notice",
        accountTitle: "My customer account"
    },
    common: {
        brandLine: "Holiday villas and apartments in Tunisia",
        save: "Save",
        saving: "Saving …",
        cancel: "Cancel",
        back: "Back",
        send: "Send",
        sending: "Sending …",
        close: "Close",
        optional: "optional",
        perNight: "per night",
        from: "from",
        onRequest: "Price on request",
        guestsUpTo: "up to {count} guests",
        bedrooms: "{count} bedrooms",
        bathrooms: "{count} bathrooms",
        sqm: "{count} m²",
        privatePool: "Private pool",
        withPool: "With pool",
        withoutPool: "Without pool",
        viewDetails: "View details",
        requestNow: "Request now",
        download: "Download",
        open: "Open",
        yes: "Yes",
        no: "No",
        nightOne: "1 night",
        nightOther: "{count} nights",
        adultOne: "1 adult",
        adultOther: "{count} adults",
        childOne: "1 child",
        childOther: "{count} children",
        accommodationOne: "1 accommodation",
        accommodationOther: "{count} accommodations",
        choose: "Please choose",
        commonCountries: "Frequently chosen",
        allCountries: "All countries"
    },
    nav: {
        home: "Home",
        accommodations: "Accommodations",
        about: "About us",
        booking: "Booking request",
        contact: "Contact",
        login: "Sign in",
        register: "Register",
        account: "My account",
        logout: "Sign out",
        openMenu: "Open menu",
        closeMenu: "Close menu",
        language: "Choose language",
        skip: "Skip to content"
    },
    types: {
        villa: "Villa",
        house: "Holiday home",
        apartment: "Holiday apartment"
    },
    amenities: {
        pool: "Private swimming pool",
        shared_pool: "Shared pool",
        sea_view: "Sea view",
        beach_access: "Close to the beach",
        wifi: "Wi-Fi",
        ac: "Air conditioning",
        heating: "Heating",
        kitchen: "Fully equipped kitchen",
        washing_machine: "Washing machine",
        tv: "Smart TV",
        parking: "Private parking",
        garden: "Garden",
        terrace: "Terrace",
        rooftop: "Roof terrace",
        courtyard: "Courtyard",
        bbq: "Barbecue",
        housekeeping: "Cleaning service",
        airport_transfer: "Airport transfer on request",
        baby_cot: "Baby cot on request",
        elevator: "Lift"
    },
    home: {
        heroEyebrow: "Zarzis · Djerba · Tunisia",
        heroTitle: "Your luxury holiday in Tunisia",
        heroText: "Hand-picked villas, holiday homes and apartments on the Mediterranean, with or without a private pool. Personally looked after by our team in Zarzis.",
        heroPrimary: "Discover accommodations",
        heroSecondary: "Send a free request",
        heroImageAlt: "Modern villa with an infinity pool, palm trees and a view of the Mediterranean",
        aboutImageAlt: "White Tunisian house with a blue door",
        searchTitle: "Check availability",
        searchDestination: "Destination",
        searchArrival: "Arrival",
        searchDeparture: "Departure",
        searchGuests: "Guests",
        searchButton: "Start request",
        anyDestination: "All destinations",
        highlights: [
            { title: "On site in Zarzis", text: "Our office is in El Mouensa near Zarzis, right where you spend your holiday." },
            { title: "Carefully selected", text: "Villas, holiday homes and apartments in beautiful locations close to the beach and town." },
            { title: "Non-binding request", text: "Your request is free of charge. It only becomes binding once we confirm it." },
            { title: "In four languages", text: "Website, customer account and emails in German, French, English and Arabic." }
        ],
        featuredEyebrow: "Our accommodations",
        featuredTitle: "Selected villas and apartments",
        featuredText: "From the villa with an infinity pool to the apartment near the beach: find the accommodation that suits you.",
        featuredAll: "View all accommodations",
        aboutEyebrow: "About us",
        aboutTitle: "At home in Zarzis, hosts by passion",
        aboutText1: "URLAUB DE LUXE TUNESIEN offers high-quality holiday villas, holiday homes and apartments in southern Tunisia. Our office is in El Mouensa near Zarzis, just a few minutes from the Mediterranean beaches.",
        aboutText2: "We give you personal and honest advice, so that you find exactly the right accommodation for your trip and enjoy a relaxed holiday from the first contact until departure.",
        aboutPoints: [
            "Based in El Mouensa, 4144 Zarzis",
            "Villas with and without a swimming pool",
            "Holiday homes and apartments",
            "Personal contact by email and customer account"
        ],
        categoriesEyebrow: "For every taste",
        categoriesTitle: "Find your accommodation",
        categoryPoolTitle: "Villas with pool",
        categoryPoolText: "Privacy, sunshine and your very own swimming pool.",
        categoryHouseTitle: "Holiday homes",
        categoryHouseText: "Charming houses with courtyard, garden and plenty of peace.",
        categoryApartmentTitle: "Holiday apartments",
        categoryApartmentText: "Comfortable apartments close to the beach and town.",
        stepsEyebrow: "It is that simple",
        stepsTitle: "Your dream holiday in three steps",
        steps: [
            { title: "Choose an accommodation", text: "Browse our villas and apartments or let our team advise you." },
            { title: "Send your request", text: "Tell us your dates, number of guests and wishes. You receive your booking number straight away." },
            { title: "Receive confirmation", text: "We check availability and confirm your booking personally by email." }
        ],
        destinationsEyebrow: "Destinations",
        destinationsTitle: "Zarzis and Djerba",
        destinations: [
            { name: "Zarzis", text: "Fine sandy beaches, olive groves and a relaxed atmosphere. Zarzis is the perfect place for peaceful days by the sea." },
            { name: "Djerba", text: "The island of white houses, palm trees and the lively market of Houmt Souk, quickly reached from Zarzis via the Roman causeway." }
        ],
        accountEyebrow: "Your customer account",
        accountTitle: "Everything in one place",
        accountText: "With your personal customer account you can keep track of requests, bookings, messages and documents at any time.",
        accountPoints: [
            "Status of your requests online at any time",
            "Confirmed and past bookings",
            "Submit complaints with photos",
            "Messages and documents from us"
        ],
        accountRegister: "Register for free",
        accountLogin: "Sign in",
        ctaTitle: "Ready for sun, sea and relaxation?",
        ctaText: "Send us your non-binding request. We will get back to you personally as soon as possible.",
        ctaButton: "Request now"
    },
    listing: {
        eyebrow: "Villas · Homes · Apartments",
        title: "Our accommodations",
        intro: "Villas with and without a pool, holiday homes and apartments in Zarzis and on Djerba.",
        filterType: "Type",
        filterAll: "All",
        filterPool: "Pool",
        filterPoolAny: "Any",
        filterLocation: "Location",
        filterLocationAll: "All locations",
        filterGuests: "Guests",
        filterGuestsAny: "Any",
        filterApply: "Show",
        filterReset: "Reset",
        empty: "We currently have no accommodation matching this selection. Send us a request anyway and we will be happy to advise you.",
        emptyButton: "Ask for advice"
    },
    detail: {
        back: "All accommodations",
        guests: "Guests",
        bedrooms: "Bedrooms",
        bathrooms: "Bathrooms",
        size: "Living space",
        pool: "Pool",
        poolYes: "Private",
        poolNo: "No",
        aboutTitle: "About this accommodation",
        amenitiesTitle: "Amenities",
        galleryTitle: "Impressions",
        bookingTitle: "Request this accommodation",
        bookingText: "Non-binding and free of charge. You receive your booking number straight away.",
        priceNote: "The final price depends on the travel dates and number of guests.",
        more: "More accommodations"
    },
    booking: {
        eyebrow: "Non-binding and free",
        title: "Booking request",
        intro: "Tell us your wishes. Your request is non-binding. The booking only becomes binding once we have confirmed it.",
        sectionTravel: "Your trip",
        sectionGuest: "Your contact details",
        sectionWishes: "Wishes and message",
        destination: "Preferred destination",
        destinationFlexible: "Flexible, please advise me",
        accommodation: "Preferred accommodation",
        accommodationAdvice: "Not decided yet, please advise me",
        arrival: "Arrival date",
        departure: "Departure date",
        adults: "Number of adults",
        children: "Number of children",
        firstName: "First name",
        lastName: "Last name",
        email: "Email address",
        phone: "Phone number",
        nationality: "Nationality",
        specialRequests: "Special requests",
        specialRequestsHint: "For example a baby cot, airport transfer or late arrival",
        message: "Additional message",
        privacyBefore: "I have read the ",
        privacyLink: "privacy policy",
        privacyAfter: " and agree to my details being processed to handle this request.",
        submit: "Send request",
        submitting: "Sending request …",
        capacityWarning: "This accommodation sleeps up to {count} guests. We are happy to suggest alternatives.",
        loggedInAs: "You are signed in as {name}. The request will automatically appear in your customer account.",
        guestHintBefore: "Tip: with a ",
        guestHintLink: "free customer account",
        guestHintAfter: " you can follow the status of your request online at any time.",
        summary: "Your selection",
        successTitle: "Thank you for your request!",
        successText: "We have received your request. Your booking number is:",
        successEmail: "A confirmation of receipt has been sent to {email}. Please also check your spam folder.",
        successNoEmail: "Please note your booking number. We will get back to you as soon as possible.",
        successAccount: "Go to my requests",
        successNew: "Send another request",
        successHome: "Back to home"
    },
    auth: {
        email: "Email address",
        password: "Password",
        showPassword: "Show password",
        hidePassword: "Hide password",
        loginTitle: "Welcome back",
        loginText: "Sign in to see your requests, bookings and messages.",
        forgot: "Forgot your password?",
        loginButton: "Sign in",
        loggingIn: "Signing in …",
        noAccount: "No customer account yet?",
        registerLink: "Register now",
        notVerifiedAction: "Confirm my email now",
        registerTitle: "Create a customer account",
        registerText: "Register for free and manage your requests and bookings conveniently online.",
        firstName: "First name",
        lastName: "Last name",
        phone: "Phone number",
        nationality: "Nationality",
        passwordConfirm: "Confirm password",
        passwordHint: "At least 8 characters",
        privacyBefore: "I have read the ",
        privacyLink: "privacy policy",
        privacyAfter: " and agree to my data being processed for my customer account.",
        marketing: "Yes, I would like to receive exclusive offers by email from time to time. I can withdraw this consent at any time.",
        registerButton: "Create account",
        registering: "Creating account …",
        haveAccount: "Already registered?",
        loginLink: "Sign in",
        verifyTitle: "Confirm your email address",
        verifyText: "We have sent a 6-digit confirmation code to {email}. Please enter it here.",
        verifyTextGeneric: "Enter your email address and the 6-digit code from our email.",
        code: "Confirmation code",
        verifyButton: "Confirm email",
        verifying: "Checking …",
        resend: "Send a new code",
        resent: "If an unconfirmed account exists for this address, a new code is on its way. Please also check your spam folder.",
        verifiedTitle: "Email address confirmed",
        verifiedText: "Thank you! Your customer account is now active.",
        emailNotSent: "Your account has been created, but the confirmation email could not be sent just now. Please request a new code below or try again later.",
        forgotTitle: "Reset your password",
        forgotText: "Enter your email address. We will send you a security code.",
        sendCode: "Send code",
        codeSent: "If a customer account exists for this address, we have sent you a code. Please also check your spam folder.",
        resetCode: "Security code",
        newPassword: "New password",
        resetButton: "Save new password",
        resetDone: "Your password has been changed. You can now sign in.",
        backToLogin: "Back to sign in",
        sideTitle: "Your holiday, personally looked after",
        sideText: "Requests, bookings, complaints and messages in one place."
    },
    errors: {
        required: "Please fill in this field.",
        invalid_email: "Please enter a valid email address.",
        invalid_phone: "Please enter a valid phone number.",
        invalid_country: "Please choose a nationality.",
        password_short: "The password must be at least 8 characters long.",
        password_mismatch: "The passwords do not match.",
        invalid_date: "Please choose a valid date.",
        date_past: "The date cannot be in the past.",
        date_order: "Departure must be after arrival.",
        stay_too_long: "A stay can last at most 90 nights.",
        invalid_number: "Please enter a valid number.",
        consent_required: "Please accept the privacy policy.",
        invalid_credentials: "The email address or password is incorrect.",
        email_not_verified: "Please confirm your email address first.",
        account_blocked: "This customer account is blocked. Please contact us.",
        email_taken: "This email address is already registered.",
        code_invalid: "The code is incorrect.",
        code_expired: "The code has expired. Please request a new one.",
        code_locked: "Too many failed attempts. Please request a new code.",
        rate_limited: "Too many attempts. Please wait a moment and try again.",
        file_type: "This file type is not supported. Images, PDF and Word documents are allowed.",
        file_too_large: "The files are too large.",
        too_many_files: "A maximum of 5 files is possible.",
        file_invalid: "The file could not be read.",
        upload_failed: "The files could not be uploaded. Please try again.",
        not_cancellable: "This request or booking can no longer be cancelled.",
        wrong_password: "The password is incorrect.",
        unauthorized: "Please sign in again.",
        network: "No connection. Please check your internet connection.",
        server_error: "Something went wrong. Please try again.",
        validation: "Please check your entries.",
        not_found: "Not found.",
        forbidden: "This action is not allowed."
    },
    bookingStatus: {
        new: "Received",
        in_review: "In progress",
        confirmed: "Confirmed",
        declined: "Declined",
        cancelled: "Cancelled",
        completed: "Completed"
    },
    complaintStatus: {
        open: "Open",
        in_progress: "In progress",
        resolved: "Resolved",
        closed: "Closed"
    },
    account: {
        areaTitle: "My customer account",
        customerNumber: "Customer number",
        nav: {
            overview: "Overview",
            profile: "My profile",
            requests: "My booking requests",
            bookings: "My confirmed bookings",
            past: "My past bookings",
            cancellations: "My cancellations",
            complaints: "My complaints",
            messages: "My messages",
            documents: "My documents",
            settings: "My settings"
        },
        hello: "Hello",
        overviewText: "Everything about your trips with us, in one place.",
        statOpenRequests: "Open requests",
        statUpcoming: "Upcoming trips",
        statComplaints: "Open complaints",
        statMessages: "New messages",
        nextTrip: "Your next trip",
        noTrip: "No confirmed trip planned yet. How about some sun and sea?",
        newRequest: "New request",
        recentRequests: "Your latest requests",
        noRequests: "You have not sent any requests yet.",
        viewAll: "View all",
        profileText: "Your personal details. We use them for your requests and bookings.",
        emailLocked: "Your email address is your login. To change it, please send us a message.",
        profileSaved: "Your details have been saved.",
        memberSince: "Registered since",
        requestsText: "Requests we are currently handling and declined requests.",
        bookingsText: "Your confirmed upcoming stays.",
        pastText: "Your past trips with us.",
        cancellationsText: "Cancelled requests and bookings.",
        requestsEmpty: "You currently have no open requests.",
        bookingsEmpty: "You currently have no confirmed bookings.",
        pastEmpty: "Your past trips will appear here.",
        cancellationsEmpty: "You have no cancellations.",
        requestedOn: "Requested on {date}",
        details: "Details",
        detailTitle: "Booking",
        timeline: "History",
        submitted: "Request received",
        yourDetails: "Your details",
        travelDetails: "Travel details",
        noteFromUs: "Message from us",
        totalPrice: "Total price",
        cancelTitle: "Cancel",
        cancelText: "Would you like to cancel this request or booking? You will receive a confirmation by email.",
        cancelReason: "Reason (optional)",
        cancelButton: "Cancel request or booking",
        cancelConfirm: "Yes, cancel now",
        cancelDone: "Your cancellation has been saved.",
        cancelledByCustomer: "Cancelled by you",
        cancelledByAdmin: "Cancelled by us",
        reportProblem: "Report a problem",
        complaintsText: "Submit a complaint and follow how it is being handled.",
        newComplaint: "New complaint",
        complaintsEmpty: "You have no complaints.",
        complaintSubject: "Subject",
        complaintBooking: "Related booking",
        complaintNoBooking: "No specific booking",
        complaintMessage: "Your message",
        complaintMessageHint: "Please describe as precisely as possible what happened.",
        files: "Photos and documents",
        filesHint: "Up to 5 files (photos, PDF, Word), 10 MB in total. Large photos are reduced automatically.",
        chooseFiles: "Choose files",
        removeFile: "Remove file",
        complaintSubmit: "Submit complaint",
        complaintSubmitted: "Your complaint {number} has been submitted.",
        complaintNumber: "Complaint number",
        submittedOn: "Submitted on",
        attachments: "Attachments",
        replyLabel: "Your reply",
        replyButton: "Send reply",
        replySent: "Your reply has been sent.",
        you: "You",
        closedNote: "This complaint is closed. If you reply, we will reopen it.",
        messagesText: "Your messages to and from us.",
        messagesEmpty: "No messages yet.",
        newMessage: "Write a new message",
        messageSubject: "Subject",
        messageBody: "Message",
        messageSend: "Send message",
        messageSent: "Your message has been sent.",
        fromUs: "From us",
        fromYou: "From you",
        documentsText: "Documents we have provided for you and the files you uploaded.",
        documentsEmpty: "No documents yet.",
        uploadedByYou: "Uploaded by you",
        providedByUs: "Provided by us",
        languageTitle: "Email language",
        languageText: "We send you our emails in this language.",
        marketingTitle: "Offers by email",
        marketingText: "I would like to receive exclusive offers by email from time to time. I can withdraw this consent at any time.",
        marketingSince: "Consent given on {date}",
        passwordTitle: "Change password",
        currentPassword: "Current password",
        newPassword: "New password",
        confirmPassword: "Confirm new password",
        passwordSaved: "Your password has been changed.",
        privacyTitle: "Privacy",
        privacyAccepted: "Privacy policy accepted on {date}",
        deleteTitle: "Delete customer account",
        deleteText: "Your account, complaints, messages and documents will be permanently deleted. Booking data will be anonymised. This cannot be undone.",
        deleteButton: "Delete customer account",
        deletePassword: "Please enter your password to confirm.",
        deleteFinal: "Delete permanently",
        deleted: "Your customer account has been deleted.",
        saved: "Saved."
    },
    contact: {
        eyebrow: "We are here for you",
        title: "Contact",
        intro: "Write to us or send a booking request directly. We will get back to you personally as soon as possible.",
        email: "Email",
        address: "Address",
        phone: "Phone",
        whatsapp: "WhatsApp",
        mapLink: "Open in Google Maps",
        bookingTitle: "Request a stay",
        bookingText: "The fastest way to your accommodation: send us your dates and wishes using the request form.",
        bookingButton: "Go to the booking request",
        accountTitle: "Already a customer?",
        accountText: "Write to us directly from your customer account, so the whole history is always at hand.",
        accountButton: "Write a message",
        faqTitle: "Frequently asked questions",
        faq: [
            { question: "Is my request binding?", answer: "No. Your request is free of charge and non-binding. It only becomes binding once we confirm your booking." },
            { question: "Do I need a customer account?", answer: "No, you can also send a request without an account. With a customer account, however, you can see the status of your requests as well as your bookings, messages and documents at any time." },
            { question: "How do I cancel a request or booking?", answer: "Sign in to your customer account and open the request or booking. You can cancel it there. Without an account, simply email us your booking number." },
            { question: "How do I submit a complaint?", answer: "In your customer account under “My complaints”. You can attach photos and documents and follow the progress at any time." }
        ]
    },
    footer: {
        about: "Holiday villas, homes and apartments in Zarzis and on Djerba, personally looked after from El Mouensa.",
        explore: "Explore",
        service: "Service",
        contact: "Contact",
        privacy: "Privacy",
        imprint: "Legal notice",
        rights: "All rights reserved."
    },
    legal: {
        privacyTitle: "Privacy policy",
        privacyUpdated: "Last updated: November 2026",
        privacy: [
            { heading: "Controller", body: "The controller responsible for processing your data on this website is URLAUB DE LUXE TUNESIEN, El Mouensa, 4144 Zarzis, Tunisia. You can reach us by email at urlaub.de.luxe.info@gmail.com." },
            { heading: "Data we process", body: "When you create a customer account, we store your first name, last name, email address, phone number and nationality. Your password is stored only as a secure, irreversible hash. For booking requests we also process the destination, accommodation, travel dates, number of guests and your wishes and messages. For complaints we store your description and the photos and documents you upload." },
            { heading: "Purposes and legal bases", body: "We process your data to answer requests, carry out bookings, provide your customer account and handle complaints (Art. 6(1)(b) GDPR). We only send offers by email with your explicit consent (Art. 6(1)(a) GDPR), which you can withdraw at any time in your customer account. To prevent misuse we process technical data such as a pseudonymised IP identifier (Art. 6(1)(f) GDPR)." },
            { heading: "Emails", body: "We send confirmations and messages via the email service of our website provider. For HTML emails it is recorded technically whether an email was opened and which links were clicked. We only use this information to make sure our emails are delivered. Addresses to which emails permanently cannot be delivered are blocked automatically." },
            { heading: "Cookies and visitor statistics", body: "We only use one technically necessary cookie so that you stay signed in. We do not use advertising or tracking cookies. For visitor statistics we use a cookie-free method that does not create personal profiles." },
            { heading: "Storage location and retention", body: "Your data is stored with our hosting provider, files in a protected, non-public storage area. We keep your data for as long as your customer account exists or statutory retention obligations require it. If you delete your account, your personal data, complaints, messages and documents are deleted and booking data is anonymised." },
            { heading: "Your rights", body: "You have the right of access, rectification, erasure, restriction of processing, data portability and objection, as well as the right to withdraw any consent at any time. You can delete your customer account yourself at any time under “My settings”. You can also lodge a complaint with a data protection supervisory authority." },
            { heading: "Data protection contact", body: "For any questions about data protection, email us at urlaub.de.luxe.info@gmail.com." }
        ],
        imprintTitle: "Legal notice",
        imprintProvider: "Provider",
        imprintRepresented: "Represented by",
        imprintContact: "Contact",
        imprintEmail: "Email",
        imprintPhone: "Phone",
        imprintResponsible: "Responsible for content",
        imprintDisclaimerTitle: "Liability for content",
        imprintDisclaimer: "The content of this website has been created with the greatest care. However, we cannot guarantee that the content is correct, complete and up to date. Only the details in our written booking confirmation are binding."
    },
    notFound: {
        title: "Page not found",
        text: "The page you are looking for does not exist or has been moved.",
        button: "Back to home"
    }
}
